import type {
  IntegrationConnectionTestResponse,
  ZoomContactCenterIntegrationRequest,
} from "@repo/app-types/entities/integration";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Input } from "@repo/ui/Inputs/input";
import { Link } from "@repo/ui/Link/index";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useTestZoomContactCenterConnectionMutation } from "../../../api/entities/zoomContactCenterIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";

const ZOOM_S2S_OAUTH_GUIDE_URL =
  "https://developers.zoom.us/docs/internal-apps/create/";

export interface ZoomConnectionFormProps {
  /** Connecting's first step on the page, or the edit dialog's body. */
  variant: "connectStep" | "editDialog";
  /** A secret here is one typed earlier in this flow, never a stored one. */
  initialCredentials?: Partial<ZoomContactCenterIntegrationRequest>;
  isDisabled?: boolean;
  onCancel?: () => void;
  /** Called with credentials that just passed the connection test. */
  onSubmit: (
    credentials: ZoomContactCenterIntegrationRequest,
  ) => void | Promise<void>;
}

/**
 * Credential entry, gated on a successful connection test: connecting's first
 * step (which continues to choosing queues) and the edit dialog.
 *
 * Saving is deliberately blocked until a test passes: credentials can be valid
 * while the OAuth app has none of the scopes the sync needs, and that failure is
 * otherwise silent — the sync looks connected and imports nothing.
 */
export const ZoomConnectionForm: FC<ZoomConnectionFormProps> = (props) => {
  const {
    variant,
    initialCredentials,
    isDisabled = false,
    onCancel,
    onSubmit,
  } = props;
  const isConnectStep = variant === "connectStep";

  const [accountId, setAccountId] = useState(
    initialCredentials?.account_id ?? "",
  );
  const [clientId, setClientId] = useState(initialCredentials?.client_id ?? "");
  const [clientSecret, setClientSecret] = useState(
    initialCredentials?.client_secret ?? "",
  );
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [hasAttemptedTest, setHasAttemptedTest] = useState(false);
  // Kept with the credentials it was for, so a test still running when the
  // fields change can't vouch for the new values once it answers.
  const [testOutcome, setTestOutcome] = useState<{
    testedCredentialsKey: string;
    result: IntegrationConnectionTestResponse;
  } | null>(null);

  const [testConnection, testState] =
    useTestZoomContactCenterConnectionMutation();

  const trimmedAccountId = accountId.trim();
  const trimmedClientId = clientId.trim();
  const trimmedClientSecret = clientSecret.trim();
  const accountIdError =
    hasAttemptedTest && !trimmedAccountId ? "Enter an account ID." : undefined;
  const clientIdError =
    hasAttemptedTest && !trimmedClientId ? "Enter a client ID." : undefined;
  const clientSecretError =
    hasAttemptedTest && !trimmedClientSecret
      ? "Enter a client secret."
      : undefined;

  const credentials: ZoomContactCenterIntegrationRequest = {
    account_id: trimmedAccountId,
    client_id: trimmedClientId,
    client_secret: trimmedClientSecret,
  };
  const currentCredentialsKey = JSON.stringify(credentials);
  // Any edit hides the previous result: it was about different credentials.
  const testResult =
    testOutcome?.testedCredentialsKey === currentCredentialsKey
      ? testOutcome.result
      : null;

  const handleTest = async () => {
    setHasAttemptedTest(true);
    if (!trimmedAccountId || !trimmedClientId || !trimmedClientSecret) return;

    setTestOutcome(null);
    let result: IntegrationConnectionTestResponse;
    try {
      result = await testConnection(credentials).unwrap();
    } catch (error) {
      result = {
        status: "failure",
        error: getApiErrorMessage(error, "Couldn't reach Zoom. Try again."),
      };
    }
    setTestOutcome({ testedCredentialsKey: currentCredentialsKey, result });
  };

  return (
    <div className="space-y-6">
      {isConnectStep ? (
        <div className="space-y-2 text-sm leading-6 text-muted-foreground">
          <p>
            Create a Server-to-Server OAuth app in the{" "}
            <Link href={ZOOM_S2S_OAUTH_GUIDE_URL}>Zoom Marketplace</Link> and
            grant it the scope{" "}
            <code className="font-mono text-xs font-bold">
              contact_center:read:list_recordings:admin
            </code>
            .
          </p>
          <p>
            To choose which queues to import, also grant the app read access to
            Contact Center queues.
          </p>
          <p>
            The Account ID, Client ID, and Client Secret are on the app&apos;s
            App Credentials page.
          </p>
        </div>
      ) : null}

      <section>
        <div className="mb-5">
          {isConnectStep ? (
            <h2 className="text-sm font-semibold text-foreground">
              Connection details
            </h2>
          ) : null}
          <p
            className={twMerge(
              "text-sm leading-5 text-muted-foreground",
              isConnectStep && "mt-1",
            )}
          >
            Test the credentials to confirm the required Zoom scopes are
            available.
          </p>
        </div>

        <div className={twMerge("space-y-5", isConnectStep && "max-w-xl")}>
          <Input
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            errorDescription={accountIdError}
            isDisabled={isDisabled}
            label="Account ID"
            onChange={(event) => {
              setAccountId(event.target.value);
            }}
            placeholder="Enter your Zoom account ID"
            required
            type="text"
            value={accountId}
          />

          <Input
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            errorDescription={clientIdError}
            isDisabled={isDisabled}
            label="Client ID"
            onChange={(event) => {
              setClientId(event.target.value);
            }}
            placeholder="Enter your Zoom client ID"
            required
            type="text"
            value={clientId}
          />

          <Input
            autoCapitalize="none"
            autoComplete="new-password"
            autoCorrect="off"
            description={
              !isConnectStep
                ? "Re-enter the client secret to update this connection — Alpharun never sends a stored secret back."
                : undefined
            }
            errorDescription={clientSecretError}
            isDisabled={isDisabled}
            label="Client secret"
            onChange={(event) => {
              setClientSecret(event.target.value);
            }}
            placeholder={
              !isConnectStep
                ? "Enter your client secret again"
                : "Enter your Zoom client secret"
            }
            required
            rightElement={
              <IconButton
                accessibleLabel={
                  isSecretVisible ? "Hide client secret" : "Show client secret"
                }
                icon={
                  isSecretVisible ? (
                    <IconEyeOff className="size-4" />
                  ) : (
                    <IconEye className="size-4" />
                  )
                }
                onClick={() => setIsSecretVisible(!isSecretVisible)}
                size={Size.SMALL}
                variant={ButtonVariant.Tertiary}
              />
            }
            type={isSecretVisible ? "text" : "password"}
            value={clientSecret}
          />
        </div>
      </section>

      {testResult ? (
        <div className={twMerge(isConnectStep && "max-w-xl")}>
          {/* The message names what to change — a rejected credential and a
              missing scope need different fixes. */}
          <InfoBanner
            description={
              testResult.status === "failure" ? testResult.error : undefined
            }
            title={
              testResult.status === "success"
                ? "Connection successful"
                : "Couldn't connect to Zoom"
            }
            variant={
              testResult.status === "success"
                ? SimpleModalVariant.Standard
                : SimpleModalVariant.Warning
            }
          />
        </div>
      ) : null}

      <div
        className={twMerge(
          "flex flex-wrap items-center justify-end gap-2",
          !isConnectStep
            ? "pt-1"
            : "max-w-xl border-t border-border-light pt-5",
        )}
      >
        {onCancel ? (
          <Button
            isDisabled={isDisabled}
            onClick={onCancel}
            variant={ButtonVariant.Tertiary}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          isDisabled={isDisabled || testState.isLoading}
          isLoading={testState.isLoading}
          onClick={() => void handleTest()}
          variant={ButtonVariant.Secondary}
        >
          {testResult?.status === "success" ? "Test again" : "Test connection"}
        </Button>
        <Button
          isDisabled={isDisabled || testResult?.status !== "success"}
          onClick={() => void onSubmit(credentials)}
          variant={ButtonVariant.Primary}
        >
          {isConnectStep ? "Continue" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};
